import { supabase } from '../lib/supabase';

class OpenAIService {
  constructor() {
    this.apiKey = process.env.REACT_APP_OPENAI_API_KEY;
    this.baseURL = 'https://api.openai.com/v1';
    this.model = 'gpt-3.5-turbo';
    
    // Debug: verificar se a chave está sendo lida
    console.log('🔑 API Key carregada:', this.apiKey ? '✅ Sim' : '❌ Não');
    console.log('🔑 Valor da chave:', this.apiKey ? `${this.apiKey.substring(0, 20)}...` : 'Nenhuma');
  }

  // Verificar se a API key está configurada
  checkAPIKey() {
    if (!this.apiKey) {
      throw new Error('Chave da API OpenAI não configurada. Configure REACT_APP_OPENAI_API_KEY no arquivo .env');
    }
  }

  // Criar sempre um novo chat para uma matéria específica
  async getOrCreateChat(alunoId, materia, escola, anoEscolar, periodo) {
    try {
      console.log(`🆕 Criando novo chat para ${materia}`);
      
      // Criar novo chat com timestamp único no título
      const timestamp = new Date().toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
      
      const { data: newChat, error: createError } = await supabase
        .from('chats')
        .insert({
          aluno_id: alunoId,
          materia,
          escola,
          ano_escolar: anoEscolar,
          periodo,
          titulo: `Chat de ${materia} - ${timestamp}`,
          status: 'ativo'
        })
        .select()
        .single();

      if (createError) {
        console.error('❌ Erro ao criar chat:', createError);
        throw createError;
      }

      console.log(`✅ Novo chat criado:`, newChat.id);
      return newChat;
    } catch (error) {
      console.error('❌ Erro ao criar chat:', error);
      throw error;
    }
  }

  // Buscar histórico de mensagens de um chat
  async getChatHistory(chatId) {
    try {
      const { data: messages, error } = await supabase
        .from('mensagens_chat')
        .select('*')
        .eq('chat_id', chatId)
        .order('data_envio', { ascending: true });

      if (error) {
        throw error;
      }

      return messages || [];
    } catch (error) {
      console.error('Erro ao buscar histórico do chat:', error);
      throw error;
    }
  }

  // Buscar contexto de IA personalizado por matéria e ano escolar
  async getContextoIA(materia, anoEscolar) {
    try {
      console.log(`🔍 Buscando contexto de IA para ${materia} - ${anoEscolar}`);
      
      const { data: contexto, error } = await supabase
        .from('contextos_ia')
        .select(`
          *,
          materias:materia_id (
            id,
            nome
          ),
          anos_escolares:ano_escolar_id (
            id,
            nome
          )
        `)
        .eq('materias.nome', materia)
        .eq('anos_escolares.nome', anoEscolar)
        .eq('ativo', true)
        .single();

      if (error) {
        console.log(`⚠️ Contexto específico não encontrado para ${materia} - ${anoEscolar}:`, error.message);
        return null;
      }

      console.log(`✅ Contexto encontrado:`, contexto.titulo);
      return contexto;
    } catch (error) {
      console.error('Erro ao buscar contexto de IA:', error);
      return null;
    }
  }

  // Buscar configuração da IA para uma matéria específica
  async getMateriaConfig(materia) {
    try {
      const { data: config, error } = await supabase
        .from('configuracao_ia_materia')
        .select('*')
        .eq('materia', materia)
        .eq('ativo', true)
        .single();

      if (error) {
        throw error;
      }

      return config;
    } catch (error) {
      console.error('Erro ao buscar configuração da matéria:', error);
      // Retornar configuração padrão se não encontrar
      return {
        prompt_sistema: `Você é um tutor especializado em ${materia} para alunos do ensino fundamental. Use linguagem clara e exemplos práticos.`,
        modelo_padrao: 'gpt-3.5-turbo',
        temperatura: 0.7,
        max_tokens: 1000
      };
    }
  }

  // Preparar contexto da conversa para a IA
  async prepareConversationContext(messages, materia, alunoNome, anoEscolar, escola) {
    // Buscar contexto personalizado da base de dados
    const contextoPersonalizado = await this.getContextoIA(materia, anoEscolar);
    
    // Contexto genérico base
    let contextContent = `Você é um tutor especializado em ${materia} para alunos do ensino fundamental.

INFORMAÇÕES DO ALUNO:
- Nome: ${alunoNome}
- Ano Escolar: ${anoEscolar}
- Escola: ${escola}
- Matéria: ${materia}

INSTRUÇÕES GERAIS:
- Use linguagem clara e apropriada para a idade do aluno
- Forneça exemplos práticos e relacionados ao contexto escolar
- Seja encorajador e paciente
- IMPORTANTE: Você APENAS pode ensinar sobre ${materia}. Se o aluno perguntar sobre outros assuntos ou matérias diferentes, responda educadamente: "Desculpe, mas eu só posso ensinar sobre ${materia}. Vamos nos atentar aos estudos de ${materia}."
- Use o nome do aluno ocasionalmente para tornar a conversa mais pessoal
- Seja educativo mas também divertido e envolvente`;

    // Se encontrou contexto personalizado, concatenar
    if (contextoPersonalizado && contextoPersonalizado.contexto) {
      console.log(`📚 Aplicando contexto personalizado: ${contextoPersonalizado.titulo}`);
      
      // Remover tags HTML do contexto se houver
      const contextoLimpo = contextoPersonalizado.contexto.replace(/<[^>]*>/g, '');
      
      contextContent += `

CONTEÚDO PROGRAMÁTICO ESPECÍFICO PARA ${materia.toUpperCase()} - ${anoEscolar.toUpperCase()}:
${contextoLimpo}

IMPORTANTE: Base suas respostas e explicações nos tópicos do conteúdo programático acima. Estes são os assuntos que você deve abordar quando ensinar ${materia} para alunos do ${anoEscolar}.`;
    } else {
      console.log(`⚠️ Usando apenas contexto genérico para ${materia} - ${anoEscolar}`);
    }

    contextContent += `

Lembre-se: Você está conversando com um aluno do ${anoEscolar} que quer aprender ${materia}. Mantenha sempre o foco nesta matéria específica.`;

    const config = {
      role: 'system',
      content: contextContent
    };

    // Converter mensagens para o formato da OpenAI
    const conversationMessages = messages.map(msg => ({
      role: msg.tipo_mensagem,
      content: msg.conteudo
    }));

    // Adicionar mensagem do sistema no início
    return [config, ...conversationMessages];
  }

  // Enviar mensagem para a OpenAI
  async sendMessage(chatId, userMessage, materia, alunoNome, anoEscolar, escola) {
    try {
      this.checkAPIKey();

      // Buscar histórico do chat
      const chatHistory = await this.getChatHistory(chatId);
      
      // Buscar configuração da matéria
      const materiaConfig = await this.getMateriaConfig(materia);
      
      // Preparar contexto da conversa
      const conversationContext = await this.prepareConversationContext(
        chatHistory, 
        materia, 
        alunoNome, 
        anoEscolar, 
        escola
      );

      // Adicionar mensagem do usuário
      const messages = [...conversationContext, { role: 'user', content: userMessage }];

      // Fazer chamada para a OpenAI
      const response = await fetch(`${this.baseURL}/chat/completions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: materiaConfig.modelo_padrao || this.model,
          messages: messages,
          temperature: materiaConfig.temperatura || 0.7,
          max_tokens: materiaConfig.max_tokens || 1000,
          stream: false
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Erro na API OpenAI: ${errorData.error?.message || response.statusText}`);
      }

      const data = await response.json();
      const aiResponse = data.choices[0].message.content;
      const tokensUsed = data.usage?.total_tokens || 0;

      // Salvar mensagem do usuário
      await this.saveMessage(chatId, 'user', userMessage, tokensUsed);

      // Salvar resposta da IA
      await this.saveMessage(chatId, 'assistant', aiResponse, tokensUsed);

      return {
        success: true,
        message: aiResponse,
        tokensUsed,
        chatId
      };

    } catch (error) {
      console.error('Erro ao enviar mensagem para OpenAI:', error);
      
      // Salvar mensagem do usuário mesmo se houver erro na IA
      try {
        await this.saveMessage(chatId, 'user', userMessage, 0);
      } catch (saveError) {
        console.error('Erro ao salvar mensagem do usuário:', saveError);
      }

      throw error;
    }
  }

  // Salvar mensagem no banco de dados
  async saveMessage(chatId, role, content, tokensUsed = 0) {
    try {
      const { error } = await supabase
        .from('mensagens_chat')
        .insert({
          chat_id: chatId,
          tipo_mensagem: role,
          conteudo: content,
          tokens_usados: tokensUsed,
          modelo_ia: this.model
        });

      if (error) {
        throw error;
      }

      return true;
    } catch (error) {
      console.error('Erro ao salvar mensagem:', error);
      throw error;
    }
  }

  // Buscar todos os chats de um aluno
  async getAlunoChats(alunoId) {
    try {
      const { data: chats, error } = await supabase
        .from('chats')
        .select(`
          *,
          mensagens_chat(count)
        `)
        .eq('aluno_id', alunoId)
        .eq('status', 'ativo')
        .order('data_ultima_atividade', { ascending: false });

      if (error) {
        throw error;
      }

      return chats || [];
    } catch (error) {
      console.error('Erro ao buscar chats do aluno:', error);
      throw error;
    }
  }

  // Fechar um chat (marcar como inativo)
  async closeChat(chatId) {
    try {
      const { error } = await supabase
        .from('chats')
        .update({ status: 'fechado' })
        .eq('id', chatId);

      if (error) {
        throw error;
      }

      return true;
    } catch (error) {
      console.error('Erro ao fechar chat:', error);
      throw error;
    }
  }

  // Renomear um chat
  async renameChat(chatId, newTitle) {
    try {
      const { error } = await supabase
        .from('chats')
        .update({ titulo: newTitle })
        .eq('id', chatId);

      if (error) {
        throw error;
      }

      return true;
    } catch (error) {
      console.error('Erro ao renomear chat:', error);
      throw error;
    }
  }
}

export default new OpenAIService();
